import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { usePreferences } from "../../context/PreferencesContext";
import { NAV_GROUPS, badgeColor, sectionBadge } from "./navConfig";

const OPEN_DELAY_MS = 90;
const CLOSE_DELAY_MS = 200;

/**
 * Primary navigation as a single-row horizontal menu bar (the desktop
 * pattern this app now follows, in place of the old strip that listed all
 * 20 pages individually with no grouping).
 *
 * Every page still gets a section: a standalone item (Command Overview)
 * renders as a direct link; everything else is grouped under its section
 * name, and that name is what sits on the bar. Hovering or clicking a
 * section opens a dropdown of its pages directly beneath it — one line of
 * chrome, seven names on it, every page still one click away.
 */
export default function MenuBar() {
  const location = useLocation();
  const { lang } = usePreferences();
  const [openKey, setOpenKey] = useState(null);
  const barRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  const scheduleOpen = useCallback((key) => {
    clearTimers();
    if (openKey !== null) { setOpenKey(key); return; }
    openTimer.current = setTimeout(() => setOpenKey(key), OPEN_DELAY_MS);
  }, [clearTimers, openKey]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimer.current = setTimeout(() => setOpenKey(null), CLOSE_DELAY_MS);
  }, [clearTimers]);

  const closeNow = useCallback(() => { clearTimers(); setOpenKey(null); }, [clearTimers]);

  // Close whenever the route changes, so the dropdown doesn't stay open
  // over the page the operator just navigated to.
  useEffect(() => { closeNow(); }, [location.pathname, closeNow]);
  useEffect(() => clearTimers, [clearTimers]);

  // Escape closes from anywhere; a click outside the bar dismisses it.
  useEffect(() => {
    if (openKey === null) return;
    const onKey = (e) => { if (e.key === "Escape") closeNow(); };
    const onPointer = (e) => { if (!barRef.current?.contains(e.target)) closeNow(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [openKey, closeNow]);

  const activeGroupKey = NAV_GROUPS.find(g =>
    g.items.some(i => location.pathname === i.path)
  )?.key;

  return (
    <nav
      aria-label="Primary navigation"
      className="hidden lg:block relative z-30 gov-gradient-chrome border-b border-saffron-dark shadow-nav"
    >
      <div className="max-w-screen-2xl mx-auto px-3">
        {/* No overflow-x here on purpose: setting only one axis of `overflow`
            forces the browser to compute the other axis as "auto" too (a
            long-standing CSS quirk), which would clip each dropdown panel
            below at this row's own height — the exact trap BMC Intelligence's
            MenuBar works around by mounting its panel as a sibling of the
            scrolling track instead of a descendant. Simpler fix here: seven
            section names comfortably fit one row at the `lg`+ widths this
            bar only renders at, so there's nothing to scroll and no
            overflow-x to opt into in the first place. */}
        <div ref={barRef} className="relative flex items-center gap-1 h-[52px]">
          {NAV_GROUPS.map((group) => {
            // Standalone item (no section) — a direct link on the bar.
            if (!group.heading) {
              const item = group.items[0];
              const Icon = item.icon;
              const active = location.pathname === item.path;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-[15px] font-semibold whitespace-nowrap transition-colors ${
                    active ? "bg-white/20 text-white" : "text-white hover:bg-white/15 hover:text-white"
                  }`}
                >
                  <Icon size={16} />
                  {item.label[lang]}
                </NavLink>
              );
            }

            const isOpen = openKey === group.key;
            const isActive = group.key === activeGroupKey;
            const { count, urgent } = sectionBadge(group.items);

            return (
              <div
                key={group.key}
                className="relative shrink-0"
                onPointerEnter={() => scheduleOpen(group.key)}
                onPointerLeave={scheduleClose}
              >
                <button
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={isOpen}
                  onClick={() => setOpenKey(isOpen ? null : group.key)}
                  className={`relative flex items-center gap-2 px-3 py-2 rounded-md text-[15px] font-semibold whitespace-nowrap transition-colors ${
                    isActive || isOpen ? "bg-white/20 text-white" : "text-white hover:bg-white/15 hover:text-white"
                  }`}
                >
                  <span>{group.heading[lang]}</span>
                  {count > 0 && (
                    <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full leading-none ${
                      urgent ? "bg-red-500 text-white" : "bg-white/25 text-white ring-1 ring-white/40 ring-inset"
                    }`}>
                      {count}
                    </span>
                  )}
                  <ChevronDown size={14} className={`text-white transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
                  <span
                    aria-hidden
                    className={`absolute inset-x-1.5 bottom-0 h-[2px] rounded-full bg-white origin-center transition-transform duration-200 ${
                      isActive ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </button>

                {isOpen && (
                  <div
                    onPointerEnter={clearTimers}
                    onPointerLeave={scheduleClose}
                    className={`absolute left-0 top-full z-40 mt-0 rounded-b-xl border border-t-0 border-slate-200 bg-white shadow-card-hover p-2 ${
                      group.items.length > 5 ? "w-[38rem] grid grid-cols-2 gap-0.5" : "w-80"
                    }`}
                  >
                    {group.items.map(item => {
                      const Icon = item.icon;
                      const active = location.pathname === item.path;
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={closeNow}
                          className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-[14px] font-semibold transition-colors ${
                            active ? "bg-saffron/10 text-saffron-dark" : "text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <Icon size={17} className={`flex-shrink-0 ${active ? "text-saffron-dark" : "text-slate-400"}`} />
                          <span className="flex-1 min-w-0 truncate">{item.label[lang]}</span>
                          {item.badge && (
                            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none flex-shrink-0 ${badgeColor(item.badge)}`}>
                              {item.badge}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
